import _ from 'lodash';
import Raven from 'raven-js';
import uuid from 'uuid';

export const LOYALTY_FLAG = 'oo-loyalty-integration';
export const LOYALTY_SIGNUP_FLAG = 'oo-loyalty-signup';

export class LoyaltyService {
    constructor(
        AuthenticationService,
        cartService,
        featureFlagService,
        restaurantInfoFactory,
        ToastApiService,
        $http,
        $q,
        $timeout
    ) {
        'ngInject';
        Object.assign(this, {
            AuthenticationService,
            cartService,
            featureFlagService,
            restaurantInfoFactory,
            ToastApiService,
            $http,
            $q,
            $timeout
        });

        this._data = {
            configPromise: null,
            restaurantGuid: null,
            signupTransactionGuid: null
        };

        restaurantInfoFactory.getInfo(['general']).then(info => {
            this._data.restaurantGuid = info.general.guid;
        });
    }

    _getCustomerLoyaltyUrl(infoType) {
        // TODO consolidate with cartService.getCartUrl(), orderInfoService._placeOrderUrl().

        const customerGuid = this.AuthenticationService.customerGuid;
        const cartGuid = this.cartService.getCartData().cart.guid;

        if (!customerGuid) {
            throw new Error('Not authenticated');
        }

        if (!cartGuid) {
            throw new Error('No cart');
        }

        return `${this.ToastApiService.data.url}/online-ordering/v1/customers/${customerGuid}/carts/enlightened/${cartGuid}/loyalty/${infoType}`;
    }

    _getCartLoyaltyUrl(infoType) {
        const cartGuid = this.cartService.getCartData().cart.guid;

        if (!cartGuid) {
            throw new Error('No cart');
        }

        return `${this.ToastApiService.data.url}/online-ordering/v1/carts/enlightened/${cartGuid}/loyalty/${infoType}`;
    }

    _getLoyaltySignupUrl() {
        const customerGuid = this.AuthenticationService.customerGuid;

        if (!customerGuid) {
            throw new Error('Not authenticated');
        }

        return `${this.ToastApiService.data.url}/customermgmt/v1/customers/${customerGuid}/loyalty`;
    }

    /**
     * @param {*} response the LoyaltyConfig from toast-online-ordering
     * @returns {*} normalized config based on the server response, will fall back to reasonable
     *  defaults if necessary
     */
    _translateConfigResponse(response = {}) {
        const vendor = _.get(response, 'enabledVendorResponse.loyaltyVendor') || 'NONE';
        const enabled = this.featureFlagService.flagEnabled(LOYALTY_FLAG) && 'NONE' !== vendor;

        const defaultVendorConfig = {
            redemptionAllowed: false,
            signupAllowed: false,
            signupBonus: null,
            accrualRate: null
        };

        let {
            redemptionAllowed,
            signupAllowed,
            signupBonus,
            accrualRate
        } = _.get(response, 'enabledVendorResponse.vendorConfigs.ONLINE') || defaultVendorConfig;

        redemptionAllowed = !!redemptionAllowed || defaultVendorConfig.redemptionAllowed;
        redemptionAllowed = redemptionAllowed && enabled;

        signupAllowed = !!signupAllowed || defaultVendorConfig.signupAllowed;
        signupAllowed = signupAllowed && enabled && this.featureFlagService.flagEnabled(LOYALTY_SIGNUP_FLAG);

        signupBonus = signupBonus || defaultVendorConfig.signupBonus;
        accrualRate = accrualRate || defaultVendorConfig.accrualRate;

        return {
            redemptionAllowed,
            signupAllowed,
            signupBonus,
            accrualRate
        };
    }

    _getConfigUrl() {
        if (!this._data.restaurantGuid) {
            throw new Error('Missing restaurant guid');
        }

        return `${this.ToastApiService.data.url}/online-ordering/v1/restaurants/${this._data.restaurantGuid}/loyaltyconfig`;
    }

    /**
     * @returns {Promise<*>} a normalized loyalty config
     */
    getConfig() {
        if (this._data.configPromise) {
            return this._data.configPromise;
        }

        let promise;

        if (this.featureFlagService.flagEnabled(LOYALTY_FLAG)) {
            promise = this.$http({
                url: this._getConfigUrl(),
                method: 'GET',
                dataType: 'JSON',
                skipCallout: true,
            }).then(
                response => response.data,
                // In case of any error, just silently make it seem like loyalty is disabled.
                error => {
                    Raven.captureMessage('Error getting loyaltyConfig', {
                        level: 'warning',
                        extra: { error }
                    });

                    return {};
                }
            );
        } else {
            promise = this.$q.resolve({});
        }

        promise = promise.then(response => this._translateConfigResponse(response));

        this._data.configPromise = promise;
        return this._data.configPromise;
    }

    /**
     * @returns {Promise<*>} a LoyaltyInquiryEnvelope from toast-online-ordering
     */
    lookup({ skipCallout = false } = {}) {
        return this.cartService
            .getCart() // Need to ensure the cart is loaded, e.g. if user hard-refreshes the page on /checkout.
            .then(() => this.$http({
                url: this._getCustomerLoyaltyUrl('lookup'),
                method: 'POST',
                dataType: 'JSON',
                skipCallout
            }));
    }

    /**
     * @returns {Promise<*>} the updated cart from toast-online-ordering
     */
    applyRedemption({
        redemptionGuid,
        skipCallout = false
    } = {}) {
        return this.cartService
            .getCart() // Need to ensure the cart is loaded, e.g. if user hard-refreshes the page on /checkout.
            .then(() => this.$http({
                url: `${this._getCustomerLoyaltyUrl('redemption')}/${redemptionGuid}`,
                method: 'POST',
                dataType: 'JSON',
                skipCallout
            }));
    }

    /**
     * @returns {Promise<*>} the updated cart from toast-online-ordering
     */
    removeLoyaltyRedemption({
        redemptionGuid,
        skipCallout = false
    } = {}) {
        return this.cartService
            .getCart() // Need to ensure the cart is loaded, e.g. if user hard-refreshes the page on /checkout.
            .then(() => this.$http({
                url: `${this._getCartLoyaltyUrl('redemption')}/${redemptionGuid}`,
                method: 'DELETE',
                dataType: 'JSON',
                skipCallout
            }));
    }

    signUp(checkGuid, skipCallout = false) {
        if (!this._data.restaurantGuid) {
            throw new Error('Missing restaurant guid');
        }

        if (!this._data.signupTransactionGuid) {
            this._data.signupTransactionGuid = uuid.v4();
        }
        return this.$http({
            url: this._getLoyaltySignupUrl(),
            method: 'POST',
            dataType: 'JSON',
            skipCallout,
            data: {
                checkIdentifier: checkGuid,
                restaurantGuid: this._data.restaurantGuid,
                transactionGuid: this._data.signupTransactionGuid
            }
        });
    }

    /**
     * @param {*} resp a LoyaltyInquiryEnvelope response from `lookup()`
     * @returns {Array}
     */
    _getAvailableRedemptionsFromResponse(resp) {
        return _.get(resp, 'response.availableRedemptions') || [];
    }

    /**
     *
     * @param {*} resp a LoyaltyInquiryEnvelope response from `lookup()`
     * @returns {number}
     */
    getAvailablePoints(resp) {
        return _(this._getAvailableRedemptionsFromResponse(resp))
            .filter(redemption => redemption.unit === 'POINT' && redemption.quantity)
            .map('quantity')
            .first() || 0;
    }

    /**
     *
     * @param {*} resp a LoyaltyInquiryEnvelope response from `lookup()`
     * @returns {number} currency value
     */
    getAvailableRedemptionAmount(resp) {
        return _(this._getAvailableRedemptionsFromResponse(resp))
            .filter(redemption => redemption.unit === 'CURRENCY' && redemption.quantity && redemption.availability != 'NOT_AVAILABLE')
            .map('quantity')
            .first() || 0;
    }

    /**
     *
     * @param {*} resp a LoyaltyInquiryEnvelope response from `lookup()`
     * @returns {boolean}
     */
    isRedemptionAvailable(resp) {
        return !!_(this._getAvailableRedemptionsFromResponse(resp))
            .filter(redemption => redemption.availability !== 'NOT_AVAILABLE'
                    && redemption.unit === 'CURRENCY' && redemption.quantity)
            .first();
    }

    /**
     * @returns {Array}
     */
    _getCheckAppliedDiscounts() {
        return _.get(this.cartService.getCartData(), 'cart.order.checks[0].appliedDiscounts') || [];
    }


    /**
     * @returns {boolean}
     */
    isRedemptionAppliedForGuid(redemptionGuid) {
        if (!redemptionGuid) {
            return false;
        }

        return !!_(this._getCheckAppliedDiscounts())
            .filter(appliedDiscount => _.hasIn(appliedDiscount, 'discount') && appliedDiscount.discount.guid === redemptionGuid)
            .first();
    }

    /**
     * @returns {boolean}
     */
    isRedemptionApplied() {
        return !!_(this._getCheckAppliedDiscounts())
            .filter(appliedDiscount => appliedDiscount.entityType === 'AppliedLoyaltyProviderDiscount')
            .first();
    }

    /**
     * @returns {number}
     */
    getAppliedRedemptionAmountForGuid(redemptionGuid) {
        if (!redemptionGuid || !this.isRedemptionAppliedForGuid(redemptionGuid)) {
            return 0;
        }

        return _(this._getCheckAppliedDiscounts())
            .filter(appliedDiscount => _.hasIn(appliedDiscount, 'discount') && appliedDiscount.discount.guid === redemptionGuid)
            .map('nonTaxDiscountAmount')
            .first() || 0;
    }

    /**
     * @returns {number}
     */
    getAppliedRedemptionAmount() {
        return _(this._getCheckAppliedDiscounts())
            .filter(appliedDiscount => appliedDiscount.entityType === 'AppliedLoyaltyProviderDiscount')
            .map('nonTaxDiscountAmount')
            .first() || 0;
    }

    /**
     *
     * @param {*} resp a LoyaltyInquiryEnvelope response from `lookup()`
     * @returns {String} in UUID format
     */
    getRedemptionGuid(resp) {
        const redemptionGuid = _(this._getAvailableRedemptionsFromResponse(resp))
            .filter(redemption => redemption.availability !== 'NOT_AVAILABLE'
                    && redemption.unit === 'CURRENCY' && redemption.quantity)
            .map('itemId')
            .first();
        if (!redemptionGuid) {
            Raven.captureMessage('No available redemption object with a guid exists', { level: 'error', extra: { resp } });
        }
        return redemptionGuid;
    }

    /**
     *
     * @param {*} resp a LoyaltyInquiryEnvelope response from `lookup()`
     * @returns {String}
     */
    getLoyaltyIdentifier(resp) {
        return _.get(resp, 'response.loyaltyIdentifier');
    }

    /**
     * @returns {boolean}
     */
    isLoyaltyAccountAttached(cart) {
        return _.hasIn(cart, 'shadowCart.order.checks[0].appliedLoyaltyInfo')
            && !!cart.shadowCart.order.checks[0].appliedLoyaltyInfo;
    }
}
